import axios from 'axios';
import { constants } from '../../Helpers/constantsFile';

/**
 * The reports the server computes for us.
 *
 * Unlike the dashboard, which pulls whole orders and works the figures out on
 * the till, an income statement is aggregated in Mongo and arrives finished.
 * That matters: the till must never be a second opinion on the shop's profit,
 * so nothing here recomputes a number the server already sent.
 *
 * Restricted to manager and accountant by `restrictTo` on the route. A cashier
 * gets a 403.
 */

const auth = (token) => ({ headers: { Authorization: token } });

/**
 * Revenue, expenses and what is left, for a period.
 *
 * The same endpoint the phone calls. Note the shape: unlike every other call
 * in this app the report IS `data` — there is no nested key beneath it.
 *
 * Both bounds are required; the server has no "everything" mode, and it
 * resolves each one to a whole calendar day, so only the date is sent.
 */
export async function fetchIncomeStatement({ businessId, start, end, token, signal }) {
  const { data } = await axios.get(
    `${constants.baseUrl}/reports/get-income-statement-by-category/${businessId}` +
      `?startDate=${start}&endDate=${end}`,
    { ...auth(token), cancelToken: signal }
  );
  return data?.data || null;
}

/**
 * The orders for a period, raw.
 *
 * The sales summary needs them because there is no sales-summary endpoint:
 * the server's report routes stop at the income statement and a per-category
 * rollup, and the phone derives its own item breakdown from exactly this call.
 * Both clients deriving it the same way from the same rows is what keeps them
 * from disagreeing.
 *
 * Cancelled orders are excluded by the server, and a waiter is scoped to their
 * own — neither is this screen's business, but both are worth knowing when the
 * figures are compared with somebody else's.
 */
export async function fetchOrdersForRange({ businessId, start, end, token, signal }) {
  const { data } = await axios.get(
    `${constants.baseUrl}/orders/get-business-orders/${businessId}` +
      `?startDate=${start}&endDate=${end}`,
    { ...auth(token), cancelToken: signal }
  );
  return data?.data?.orders || [];
}

/* ── reading what comes back ────────────────────────────────────────────── */

/**
 * A figure off the report.
 *
 * Numbers normally arrive as JSON numbers, but a total large enough to
 * overflow Mongo's double comes back as a string — the phone's model handles
 * the same case for the same reason.
 */
export function reportNumber(raw) {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * One line of a breakdown, from either side of the report.
 *
 * Revenue lines carry `gross` keyed by `categoryId`; expense lines carry
 * `amount` keyed by `expenseTypeId`. Everything else about them is identical,
 * so they are flattened into one shape here rather than in two components.
 *
 * `id` is null on the catch-all buckets the server returns — "Uncategorized",
 * "General" — and that null is what marks a line as not really a category.
 */
function normaliseLine(line, idKey, amountKey) {
  const id = line?.[idKey];
  return {
    id: id === null || id === undefined ? null : String(id),
    name: String(line?.name || ''),
    amount: reportNumber(line?.[amountKey]),
    share: reportNumber(line?.percentage),
  };
}

/** The whole report, in the shape the screen reads. */
export function normaliseStatement(raw) {
  if (!raw) return null;

  const revenue = raw.revenue || {};
  const expenses = raw.expenses || {};

  return {
    revenue: {
      gross: reportNumber(revenue.grossRevenue),
      discounts: reportNumber(revenue.discounts),
      net: reportNumber(revenue.netRevenue),
      orderCount: Math.round(reportNumber(revenue.orderCount)),
      categories: (revenue.categories || []).map((line) =>
        normaliseLine(line, 'categoryId', 'gross')
      ),
    },
    expenses: {
      total: reportNumber(expenses.total),
      count: Math.round(reportNumber(expenses.count)),
      categories: (expenses.categories || []).map((line) =>
        normaliseLine(line, 'expenseTypeId', 'amount')
      ),
    },
    netIncome: reportNumber(raw.netIncome),
  };
}

/**
 * Nothing moved either way.
 *
 * Checked against the GROSS figures so a period whose revenue and expenses
 * happen to cancel out is still reported rather than shown as empty — the
 * same test the phone's model makes.
 */
export function isEmptyStatement(statement) {
  if (!statement) return true;
  return statement.revenue.gross === 0 && statement.expenses.total === 0;
}
